import CryptoJS from 'crypto-js';
import querystring from 'querystring';
import { QcTip, QcNotification, QcDialog } from '@tencent/qcvue2';
import moment from 'moment';
import { CONSTANT } from './config';

function uriEscape(string) {
  let output = encodeURIComponent(string);
  output = output.replace(/[^A-Za-z0-9_.~\-%]+/g, escape);

  output = output.replace(
    /[*]/g,
    ch => `%${ch
      .charCodeAt(0)
      .toString(16)
      .toUpperCase()}`,
  );

  return output;
}

export function queryStringParse(qs) {
  return querystring.parse(qs);
}

export function queryParamsToString(params) {
  const items = [];
  const escape = uriEscape;
  const sortedKeys = Object.keys(params).sort();

  sortedKeys.forEach(name => {
    const value = params[name];
    const ename = escape(name);
    let result = `${ename}=`;
    if (Array.isArray(value)) {
      const vals = [];
      value.forEach(item => {
        vals.push(escape(item));
      });
      result = `${ename}=${vals.sort().join(`&${ename}=`)}`;
    } else if (value !== undefined && value !== null) {
      result = `${ename}=${escape(value)}`;
    }
    items.push(result);
  });

  return items.join('&');
}

/**
 * AES 解码
 * @param {String} word 要解码的文本
 * @param {String} key 用来解码的密钥
 */
const iv = CryptoJS.enc.Utf8.parse(CONSTANT.IV); // 密钥偏移量
export function AESDecrypt(word, key) {
  return new Promise(resolve => {
    try {
      const accessCode = CryptoJS.enc.Utf8.parse(key); // 密钥
      const decrypt = CryptoJS.AES.decrypt(word, accessCode, {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      const decryptedStr = CryptoJS.enc.Utf8.stringify(decrypt).toString();
      resolve(decryptedStr);
    } catch (error) {
      resolve();
    }
  });
}

/**
 * 解码 base64
 * @param {String} base64
 */
export function decodeBase64(base64) {
  const decode = CryptoJS.enc.Base64.parse(base64);
  const str = CryptoJS.enc.Utf8.stringify(decode);
  return str;
}

export function encodeBase64(str) {
  const encode = CryptoJS.enc.Utf8.parse(str);
  const base64 = CryptoJS.enc.Base64.stringify(encode);
  return base64;
}

/**
 * tips 提示
 */
export const tips = {
  success(msg) {
    QcTip.success({ message: msg, duration: 2500 });
  },
  error(msg, content, time) {
    let duration = time;
    if (typeof duration === 'undefined') duration = 3000;
    if (!content) {
      return QcTip.error({ message: msg, duration });
    }
    return QcTip.error({ content, duration });
  },
  loading(msg) {
    if (!this.loadingTipHandler) {
      this.loadingTipHandler = QcTip.createTip({ type: 'loading', message: msg, duration: 0 });
      this.loadingTipHandler.update({ show: true });
    }
    const tipHandler = this.loadingTipHandler;
    tipHandler.update({
      message: msg,
    });
  },
  tips(options) {
    QcTip(options);
  },
  closeTips() {
    QcTip.close();
    if (this.loadingTipHandler) {
      this.loadingTipHandler.closeAndDestroy();
      this.loadingTipHandler = null;
    }
  },
  notification({ message, type = 'default', content = '' }) {
    const closeNotification = QcNotification.showNotification({
      message,
      content,
      type,
      duration: 0,
      onShowUpdate(show) {
        if (!show) {
          closeNotification();
        }
      },
    });
    return closeNotification;
  },
};

/**
 * 获取组件中使用的 props 的代理
 * @param {String} key
 * @param {Object} opt
 */
export function getInnerProps(key, opt) {
  const options = opt || { trim: true };

  const updateKey = `update:${key}`;

  return {
    get() {
      return this[key];
    },
    set(val) {
      let value = val;
      if (typeof val === 'string' && options.trim) {
        value = val.trim();
      }

      this.$emit(updateKey, value);
    },
  };
}

/**
 * 点击链接下载文件
 * @param {String} url
 */
export function downloadUrl(url) {
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = !!ua.match(/\(i[^;]+;( u;)? cpu.+mac os x/);
  // 如果在手机QQ中打开，则提示在safari中打开
  if (isIOS && /qq/i.test(ua)) {
    const { lang } = window;
    QcDialog.alert({
      icon: 'none',
      title: lang.t('cos.open_in_safari_to_download'),
    });
  } else {
    // 使用 iframe 方式下载
    const id = 'downloadCosFile';
    const iframe = document.getElementById(id);
    iframe.id = 'downloadCosFile';
    iframe.src = url;
  }

  // 使用 a 标签方式下载
  // const aTag = document.createElement('a');
  // aTag.target = '_blank';
  // aTag.href = url;
  // document.body.appendChild(aTag);
  // aTag.click();
  // setTimeout(() => {
  //   document.body.removeChild(aTag);
  // }, 100);
}

/**
 * 将 UTC 时间转换为时间戳
 * @param {String} date
 */
export function dateToUnix(date) {
  if (!date) return '-';
  return moment(date).unix();
}

/**
 * 格式化秒
 * @param {Number} second
 * @param {Number} num
 */
export function formatSecond(second, num) {
  const { lang } = window;
  const timeUnit = {
    ms: lang.t('core.time_unit.ms'),
    s: lang.t('core.time_unit.s'),
    min: lang.t('core.time_unit.min'),
    h: lang.t('core.time_unit.h'),
    d: lang.t('core.time_unit.d'),
    y: lang.t('core.time_unit.y'),
  };
  const unit = [timeUnit.ms, timeUnit.s, timeUnit.min, timeUnit.h, timeUnit.d, timeUnit.y];
  const d = [1000, 60, 60, 24, 365];
  let n = second * 1000;
  let index = 0;
  for (index = 0; index < unit.length && n >= d[index]; index += 1) n /= d[index];
  return n.toFixed(num || 0) + unit[index];
}

/**
 * 判断传入的参数是否为数组类型
 * @param {*} arr
 */
export function isArray(arr) {
  return arr instanceof Array;
}

/**
 * 空函数
 */
export function noop() {}

/**
 * 求字符串的字节数
 * @param {String} str
 * @param {String} charset
 */
export function getStringBytes(str, charset) {
  let total = 0;
  let charCode;
  charset = charset ? charset.toLowerCase() : '';
  if (charset === 'utf-16' || charset === 'utf16') {
    for (let i = 0, len = str.length; i < len; i++) {
      charCode = str.charCodeAt(i);
      if (charCode <= 0xffff) {
        total += 2;
      } else {
        total += 4;
      }
    }
  } else {
    for (let i = 0, len = str.length; i < len; i++) {
      charCode = str.charCodeAt(i);
      if (charCode <= 0x007f) {
        total += 1;
      } else if (charCode <= 0x07ff) {
        total += 2;
      } else if (charCode <= 0xffff) {
        total += 3;
      } else {
        total += 4;
      }
    }
  }
  return total;
}

/**
 * 获取数字对应的字符串描述（单位：KB MB GB TB PB）
 * @param {Number} size
 * @param {Number} base
 */
export function getSizeStr(size, base) {
  if (Number.isNaN(size)) {
    return '';
  }
  base = base * 1 || 1024;
  let result = '';
  const KBlevel = base;
  const MBlevel = KBlevel * base;
  const GBlevel = MBlevel * base;
  const TBlevel = GBlevel * base;
  const PBlevel = TBlevel * base;
  // size是Byte
  if (size < KBlevel) {
    result = `${size}B`;
  } else if (size < MBlevel) {
    result = `${(size / KBlevel).toFixed(2)}KB`;
  } else if (size < GBlevel) {
    result = `${(size / MBlevel).toFixed(2)}MB`;
  } else if (size < TBlevel) {
    result = `${(size / GBlevel).toFixed(2)}GB`;
  } else if (size < PBlevel) {
    result = `${(size / TBlevel).toFixed(2)}TB`;
  } else {
    result = `${(size / PBlevel).toFixed(2)}PB`;
  }
  return result;
}

/**
 * 获取拖拽事件中本地上传的文件列表
 * @param {DirectoryEntry} directory
 */
export function readDirectory(directory) {
  const files = [];
  let pending = 0;

  function getFiles(dirEntry, resolve, reject) {
    const dirReader = dirEntry.createReader();
    let entries = [];
    pending++;

    const getEntries = () => {
      dirReader.readEntries(results => {
        if (results.length) {
          entries = entries.concat(Array.from(results));
          getEntries();
        } else {
          pending--;
          entries.forEach(entry => {
            if (entry.isFile) {
              pending++;
              entry.file(File => {
                File.fullPath = entry.fullPath.replace('/', ''); // 去掉第一个/
                files.push(File);
                pending--;

                if (pending === 0) {
                  resolve(files);
                }
              }, reject);
            } else {
              const FolderObj = {
                isFolder: true,
                size: 0,
                fullPath: `${entry.fullPath.replace('/', '')}/`, // 去掉第一个/，尾部加上/
              };
              files.push(FolderObj);
              getFiles(entry, resolve, reject);
            }
          });

          if (pending === 0) {
            resolve(files);
          }
        }
      }, reject);
    };

    getEntries();
  }

  return new Promise((resolve, reject) => {
    getFiles(directory, resolve, reject);
  });
}

/**
 * 路径处理
 */
export const path = {
  extname(filename) {
    // 文件扩展名匹配正则
    const reg = /\.[^\.]+$/;
    const matches = reg.exec(filename);
    if (matches) {
      return matches[0];
    }
    return '';
  },
  dirname(filename) {
    const index = filename.lastIndexOf('/');
    return filename.substr(0, index);
  },
  basename(filename) {
    const index = filename.lastIndexOf('/');
    const ext = this.extname(filename);
    return filename.substr(index + 1).replace(ext, '');
  },
};

/**
 * 功能：从 list 中移除与 removeList 中 key 相同的项
 * @param {*} removeList
 * @param {*} list
 */
export function removeFromList(removeList, list, removeKeys) {
  let _removeKeys = ['key', 'Key', 'versionId', 'VersionId'];
  if (removeKeys) {
    _removeKeys = _removeKeys.concat(removeKeys);
  }

  const needPopIdMap = {};
  removeList.forEach(item => {
    let key = '';
    _removeKeys.forEach(k => {
      key += item[k] || '';
    });
    needPopIdMap[key] = true;
  });
  // 从后往前移除
  for (let i = list.length - 1; i >= 0; i--) {
    let key = '';
    _removeKeys.forEach(k => {
      key += list[i][k] || '';
    });
    if (needPopIdMap[key]) {
      list.splice(i, 1);
    }
  }
  return list;
}
